import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import { ShieldCheck, Users, UserCog } from 'lucide-react';
import { UsersTable } from './UsersTable';
import { RolesTable } from './RolesTable';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const HeaderCard = styled.div`
  padding: 20px 22px;
  border-radius: 18px;
  border: 1px solid ${p => p.theme.border};
  background: ${p => p.theme.card};
  box-shadow: ${p => p.theme.shadowSoft};

  @media (max-width: 640px) {
    padding: 16px 18px;
  }

  @media (max-width: 480px) {
    padding: 14px 14px;
  }
`;

const HeaderTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;

  @media (max-width: 640px) {
    flex-direction: column;
  }
`;

const HeaderCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const HeaderBadge = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${p => p.theme.primary};
  background: ${p => p.theme.muted};
  border: none;
`;

const HeaderTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: ${p => p.theme.foreground};

  @media (max-width: 640px) {
    font-size: 16px;
  }

  @media (max-width: 480px) {
    font-size: 15px;
  }
`;

const HeaderDescription = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.6;
  color: ${p => p.theme.mutedForeground};
  max-width: 680px;

  @media (max-width: 480px) {
    font-size: 12px;
    line-height: 1.5;
  }
`;

const SummaryRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 16px;
`;

const SummaryPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  color: ${p => p.theme.foreground};
  background: ${p => p.theme.muted};
  border: none;
`;

const TabsContainer = styled.div`
  display: inline-flex;
  gap: 4px;
  width: fit-content;
  padding: 5px;
  border-radius: 14px;
  background: ${p => p.theme.muted};
  border: none;

  @media (max-width: 480px) {
    width: 100%;
    gap: 4px;
    justify-content: flex-start;
    overflow-x: auto;
  }
`;

const Tab = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 11px 16px;
  background: ${props => (props.$active ? props.theme.card : 'transparent')};
  border: none;
  outline: none;
  box-shadow: none;
  border-radius: 10px;
  color: ${props =>
    props.$active ? props.theme.foreground : props.theme.mutedForeground};
  font-weight: ${props => (props.$active ? 700 : 600)};
  font-size: 13px;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover {
    color: ${p => p.theme.foreground};
    background: ${p => (p.$active ? p.theme.card : p.theme.muted)};
  }

  @media (max-width: 480px) {
    padding: 9px 12px;
    font-size: 12px;
    gap: 6px;
  }
`;

const TabContent = styled.div`
  margin-top: 0;
`;

const ContentCard = styled.div`
  padding: 18px;
  border-radius: 18px;
  border: 1px solid ${p => p.theme.border};
  background: ${p => p.theme.card};
  box-shadow: ${p => p.theme.shadowSoft};

  @media (max-width: 640px) {
    padding: 14px;
  }

  @media (max-width: 480px) {
    padding: 12px;
  }
`;

const SectionTitle = styled.div`
  margin-bottom: 14px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SectionHeading = styled.h4`
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: ${p => p.theme.foreground};

  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

const SectionDescription = styled.p`
  margin: 0;
  font-size: 12.5px;
  line-height: 1.5;
  color: ${p => p.theme.mutedForeground};

  @media (max-width: 480px) {
    font-size: 11.5px;
    line-height: 1.45;
  }
`;

interface TeamManagementProps {}

export const TeamManagement: React.FC<TeamManagementProps> = () => {
  const { user } = useAuth();
  const teamTabKey = useMemo(
    () => `landlordSettings:teamTab:${user?.id ?? 'anon'}`,
    [user?.id],
  );
  const [activeTab, setActiveTab] = useState<'users' | 'roles'>('users');

  useEffect(() => {
    try {
      const v = localStorage.getItem(teamTabKey);
      if (v === 'users' || v === 'roles') setActiveTab(v);
    } catch {
      /* ignore */
    }
  }, [teamTabKey]);

  useEffect(() => {
    try {
      localStorage.setItem(teamTabKey, activeTab);
    } catch {
      /* ignore */
    }
  }, [activeTab, teamTabKey]);

  const isUsersTab = activeTab === 'users';

  return (
    <Container>
      <HeaderCard>
        <HeaderTop>
          <HeaderCopy>
            <HeaderTitle>Team workspace</HeaderTitle>
            <HeaderDescription>
              Manage staff access, review who can work inside your account, and keep permissions organized from one place.
            </HeaderDescription>
          </HeaderCopy>
          <HeaderBadge>
            <UserCog size={20} />
          </HeaderBadge>
        </HeaderTop>

        <SummaryRow>
          <SummaryPill>
            <Users size={14} />
            Invite and manage members
          </SummaryPill>
          <SummaryPill>
            <ShieldCheck size={14} />
            Control roles and permissions
          </SummaryPill>
        </SummaryRow>
      </HeaderCard>

      <TabsContainer>
        <Tab
          $active={isUsersTab}
          onClick={() => setActiveTab('users')}
        >
          <Users size={15} />
          Users
        </Tab>
        <Tab
          $active={activeTab === 'roles'} 
          onClick={() => setActiveTab('roles')}
        >
          <ShieldCheck size={15} />
          Roles
        </Tab>
      </TabsContainer>

      <TabContent>
        <ContentCard>
          <SectionTitle>
            <SectionHeading>{isUsersTab ? 'Team members' : 'Access roles'}</SectionHeading>
            <SectionDescription>
              {isUsersTab
                ? 'View active users, invite collaborators, and manage account access for your team.'
                : 'Define responsibilities and keep permissions consistent across your organization.'}
            </SectionDescription>
          </SectionTitle>

          {isUsersTab && <UsersTable />}
          {activeTab === 'roles' && <RolesTable />}
        </ContentCard>
      </TabContent>
    </Container>
  );
};

